import { AccountOptionsQuery } from '@cma/generated/graphql'
import { HelixIcon } from '@helix/helix-icon'
import {
  building_bank as BankIcon,
  building_modern as BuildingIcon,
  wolfie as WolfieIcon,
  home as HomeIcon,
  user as UserIcon
} from '@helix/helix-icon/outlined'
import {
  SelectionBox,
  SelectionBoxOption,
  SelectionBoxOptionObjectProps
} from '@helix/selection-box'
import { formatDistanceToNowStrict } from 'date-fns'
import pluralize from 'pluralize'
import { ComponentType, useCallback, useMemo } from 'react'
import { Avatar } from './Avatar'
import styles from './AccountSelectionList.module.css'
import { classNames } from './utils'

// The fields the AccountOptions query selects, not the full schema type,
// which has required fields this list never fetches.
export type AccountOption = AccountOptionsQuery['accountOptions'][number]

interface AccountSelectionListProps {
  accounts: AccountOption[]
  value?: string
  onChange(account: AccountOption): void
}

function helixIcon(
  icon: typeof UserIcon
): ComponentType<{ className?: string }> {
  return function Icon({ className }) {
    return <HelixIcon icon={icon} className={className} />
  }
}

const accountTypes: Record<
  string,
  {
    label: string
    Icon: ComponentType<{ className?: string }>
    iconClassName?: string
  }
> = {
  platform_ecommerce: { label: 'Paid by you', Icon: helixIcon(UserIcon) },
  agent_bundle: { label: 'Paid by you', Icon: helixIcon(UserIcon) },
  legacy_single: { label: 'Paid by you', Icon: helixIcon(UserIcon) },
  platform_client_brokerage: {
    label: 'Lone Wolf Platform account',
    Icon: helixIcon(WolfieIcon),
    iconClassName: styles.wolfieIcon
  },
  v2_broker: {
    label: 'Provided by your brokerage',
    Icon: helixIcon(BuildingIcon)
  },
  legacy_small_broker: {
    label: 'Provided by your brokerage',
    Icon: helixIcon(BuildingIcon)
  },
  mls_site_license: {
    label: 'Provided by your MLS',
    Icon: helixIcon(BankIcon)
  },
  affiliate_store: { label: 'Partner account', Icon: helixIcon(HomeIcon) }
}

function getLastReportLabel({
  totalReports,
  lastReportCreatedAt
}: AccountOption) {
  if (!totalReports || !lastReportCreatedAt) return 'No reports yet'
  return `Last report ${formatDistanceToNowStrict(
    new Date(lastReportCreatedAt)
  )} ago`
}

export function AccountSelectionList({
  accounts,
  value,
  onChange
}: AccountSelectionListProps) {
  const sortedAccounts = useMemo(
    () =>
      [...accounts].sort((a, b) => {
        const aTime = a.lastReportCreatedAt
          ? new Date(a.lastReportCreatedAt).getTime()
          : -Infinity
        const bTime = b.lastReportCreatedAt
          ? new Date(b.lastReportCreatedAt).getTime()
          : -Infinity
        return bTime - aTime
      }),
    [accounts]
  )

  const selectedCards = useMemo(
    () => (value ? { id: `account-${value}`, value } : undefined),
    [value]
  )

  const handleChange = useCallback(
    (
      selection: SelectionBoxOptionObjectProps | SelectionBoxOptionObjectProps[]
    ) => {
      if (Array.isArray(selection)) return
      const account = accounts.find(
        ({ accountId }) => accountId === selection.value
      )
      if (account) onChange(account)
    },
    [accounts, onChange]
  )

  return (
    <div className={styles.scrollContainer}>
      <SelectionBox
        className={classNames(styles.list, '!flex-col')}
        value={selectedCards}
        onChange={handleChange}>
        {sortedAccounts.map((account) => {
          const { label, Icon, iconClassName } = accountTypes[
            account.accountType
          ] ?? { label: '', Icon: helixIcon(UserIcon) }

          return (
            <SelectionBoxOption
              key={account.accountId}
              id={`account-${account.accountId}`}
              value={account.accountId}>
              <div className="flex items-start space-x-3">
                <span className="helix-bg-gray-100 helix-text-gray-600 flex h-8 w-8 shrink-0 items-center justify-center rounded-md">
                  <Icon
                    className={classNames('h-5 w-5', iconClassName || '')}
                  />
                </span>
                <div className="min-w-0 flex-1 space-y-0.5 text-left">
                  <div className="flex items-start justify-between">
                    <div className="helix-body--strong helix-text-gray-900 min-w-0">
                      {account.name}
                    </div>
                    {(account.accountLogo || account.userLogo) && (
                      <div className="ml-2 flex shrink-0 -space-x-2">
                        {account.accountLogo && (
                          <Avatar
                            src={account.accountLogo}
                            name={account.name}
                            size="xs"
                            outline
                          />
                        )}
                        {account.userLogo && (
                          <Avatar src={account.userLogo} size="xs" outline />
                        )}
                      </div>
                    )}
                  </div>
                  <div className="helix-body helix-text-gray-600 flex items-center space-x-2">
                    {!!label && <span>{label}</span>}
                    {account.role === 'Admin' && (
                      <span className="helix-badge helix-badge--inverted helix-badge--tiny">
                        Admin
                      </span>
                    )}
                  </div>
                  <div className="helix-small helix-text-gray-500">
                    {account.totalReports}{' '}
                    {pluralize('report', account.totalReports)} &middot;{' '}
                    {getLastReportLabel(account)}
                  </div>
                </div>
              </div>
            </SelectionBoxOption>
          )
        })}
      </SelectionBox>
    </div>
  )
}
